// Copyright (c) Docugami, Inc. All rights reserved.
import { useState, useEffect } from 'react';
import { Text } from "@fluentui/react";
import { useSelector } from 'react-redux';
import { AppState } from '../../store';
import { ClauseSuggestionsList } from './ClauseSuggestionsList';
import { WordFieldsCard } from './WordFieldsCard';
import { primaryTheme, primaryThemeSettings } from '../shell/presentation/AppStyles';
import { useDgIcons, dgIconNames } from '../../hooks/dgIcons';
import { DgIconButton } from '../shell/utils/DgIconButton';
import { useAaDgWordCanvasProxy } from '../../hooks/aaDgWordCanvasProxy';
import { DgOnSelectionEventArgs } from '../doc_hosting/doc_hosts/word/v3/DgOnSelectionEventArgs';
import { AaTaskpaneMenu } from './AaTaskpaneMenu';
import styled from 'styled-components';
import { DgNonSelectableLabelCss } from '../common/DgCommonStyledComponents';
import { getLocalStorageAssistedAuthoringContextModeValue } from '../../utils/localStorageHelpers';
import { setAssistedAuthoringSettings } from '../../store/settings/actions';
import { ChunkInstance } from '../../networking/api';
import StatusBar from '../file_management/status-bar';
import { selectChunkThunk } from 'hooks/chunkSelection';
import { useDocumentDgml } from 'hooks/documentDgml';
import { Logger, LoggerCategory } from 'utils/Logger';
import { Telemetry } from 'telemetry/TelemetryManager';
import { useTelemetryUserContext } from 'hooks/TelemetryContext';
import { dgFontFamily } from 'components/common/Fonts';
import { DgKnownChunk } from 'components/doc_hosting/doc_hosts/word/v3/DgParagraphMetadata';
import { useAppDispatch } from 'hooks/redux';
import { useDgAuth } from 'hooks/DgAuthentication';
import { ClauseLibrary } from './ClauseLibrary';
import { setKnownChunks, useFetchAssistedAuthoringInfosQuery } from 'store/assistedauthinfo/assistedAuthoringSlice';

enum TaskpanePages {
    None,
    Clauses,
    Variations,
    Fields,
    ClauseLibrary
}

interface ITaskpanePage {
    page: TaskpanePages;
    label: string;
    icon: dgIconNames;
    iconHeight: number;
    iconWidth: number;
}

const headerHeight = '50px';

const AaHeaderLabelText = styled.span`
    font-family: ${dgFontFamily};
    font-style: normal;
    font-weight: normal;
    font-size: 8px;
    line-height: 10px;
    display: flex;
    align-items: center;
    text-align: center;
    ${DgNonSelectableLabelCss}
    color: ${primaryTheme.palette.white};
`;


export interface WordTaskpaneProps {
    documentId: string;
}

export const WordTaskpane = (props: WordTaskpaneProps): JSX.Element => {
    const [selectedChunkId, setSelectedChunkId] = useState("");
    const [, setViewAllForLabel] = useState("");
    const [currentField, setCurrentField] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const [dgWordCanvasProxyInitialized, setDgWordCanvasProxyInitialized] = useState(false);
    const [contextSpanIds, setContextSpanIds] = useState<string[]>([]);

    const [activePage, setActivePage] = useState(TaskpanePages.Clauses);
    const [selectionEventArgs, setSelectionEventArgs] = useState<DgOnSelectionEventArgs | null>(null);

    const aaSettings = useSelector((state: AppState) => state.settings.assistedAuthoring);

    const curOrg = useSelector((state: AppState) => state.organizations.currentOrganization);

    const { user } = useDgAuth();

    const dispatch = useAppDispatch();
    const dgWordCanvasProxy = useAaDgWordCanvasProxy(props.documentId);
    const dgml = useDocumentDgml();
    useTelemetryUserContext();

    const { isLoading: isAssistedAuthoringInfoLoading, error: assistedAuthoringInfosFailedDownload } = useFetchAssistedAuthoringInfosQuery(props.documentId, { skip: !dgml.isInitialized() });

    // Telemetry: initialize for word add-in
    useEffect(() => {
        if (!!user?.email && !!curOrg?.id) {
            Telemetry.setUserContext(user.email, curOrg.id)
        }
    }, [user, curOrg])

    useEffect(() => {
        let contextMode = getLocalStorageAssistedAuthoringContextModeValue();
        dispatch(setAssistedAuthoringSettings({ ...aaSettings, contextMode: contextMode }));
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [])

    useEffect(() => {
        async function initializeKnownChunks() {
            const dgKnownChunks = await dgWordCanvasProxy!.getKnownChunks();
            dispatch(setKnownChunks(dgKnownChunks ?? []));
        }

        if (!dgWordCanvasProxyInitialized && !!dgWordCanvasProxy && dgml.isInitialized()) {
            setDgWordCanvasProxyInitialized(true);
            dgWordCanvasProxy.onSelection.on((dgEventArgs: any) => {
                return setSelectionEventArgs(dgEventArgs)
            });

            // fire & forget
            initializeKnownChunks();

            dgWordCanvasProxy.onKnownChunksChange.on((dgKnownChunks: DgKnownChunk[] | undefined) => {
                dispatch(setKnownChunks(dgKnownChunks!));
            });

            if (aaSettings.contextMode) {
                // help with the case that we are contextual mode and we wind up with an empty taskpane
                const rootSpanId = dgml.getRootSpanId();
                setSelectionEventArgs({
                    DgmlSpanId: rootSpanId,
                    ParagraphDgmlSpanId: rootSpanId,
                    PreviousParagraphDgmlSpanId: rootSpanId,
                    IsField: false,
                    SelectionLength: 0
                })
            }
        }
    }, [dgWordCanvasProxyInitialized, dgWordCanvasProxy, dgml, aaSettings.contextMode, dispatch])

    useEffect(() => {
        async function handleSelectionChangeAsync() {
            if (selectionEventArgs && dgml.isInitialized() && dgWordCanvasProxy !== null) {
                setSelectionEventArgs(null);

                let chunk: ChunkInstance | null = null;
                let spanIds = await dgWordCanvasProxy.getContextSpanIds(selectionEventArgs.DgmlSpanId);
                if (!!selectionEventArgs.PreviousParagraphDgmlSpanId && selectionEventArgs.PreviousParagraphDgmlSpanId !== selectionEventArgs.DgmlSpanId) {
                    let previousSpandIds = await dgWordCanvasProxy.getContextSpanIds(selectionEventArgs.PreviousParagraphDgmlSpanId);
                    spanIds = [...spanIds, ...previousSpandIds];

                    if (!selectionEventArgs.IsField) {
                        chunk = dgml.getInstanceById(selectionEventArgs.PreviousParagraphDgmlSpanId);
                    }
                }
                setContextSpanIds(spanIds);

                if (!chunk) {
                    chunk = dgml.getInstanceById(selectionEventArgs.DgmlSpanId);
                    if (!chunk) {
                        return;
                    }
                }

                if (selectionEventArgs.IsField) {
                    Logger.LogInformation(LoggerCategory.AssistedAuthoring, `WordTaskPane: Field Selection in dgEventArgs.DgmlSpanId: ${selectionEventArgs.DgmlSpanId}`);
                    currentFieldChangeCallback(chunk.spanId!);
                }
                else {
                    Logger.LogInformation(LoggerCategory.AssistedAuthoring, `WordTaskPane: Selection in dgEventArgs.DgmlSpanId: ${selectionEventArgs.DgmlSpanId}`);
                    selectedChunkChangeCallback(chunk.spanId!);
                }
            }
        }

        // fire and forget async function
        handleSelectionChangeAsync();
    },
        [selectionEventArgs, dgml, dgWordCanvasProxy])

    function viewAllVariations(label: string): void {
        setViewAllForLabel(label);

        if (label !== "") {
            setActivePage(TaskpanePages.Variations);
        }
    }

    function selectedChunkChangeCallback(chunkId: string): void {
        setSelectedChunkId(chunkId);
    }

    function currentFieldChangeCallback(chunkId: string): void {
        setCurrentField(chunkId);
    }

    useEffect(() => {
        dispatch(selectChunkThunk(selectedChunkId));
    }, [selectedChunkId, dispatch])

    function getPageContent(): JSX.Element {
        if (!dgml.isInitialized()) {
            return <WordTaskpaneLoadingPage />;
        }

        switch (activePage) {
            case TaskpanePages.Clauses:
                if (isAssistedAuthoringInfoLoading || !dgWordCanvasProxy) {
                    return <WordTaskpaneLoadingPage />;
                } else if (assistedAuthoringInfosFailedDownload) {
                    return <div><Text variant="xLarge">Failed to Analyze Document</Text></div>
                } else {
                    return (
                        <ClauseSuggestionsList
                            viewAllVariations={viewAllVariations}
                            spanIdsInContext={contextSpanIds}
                            documentId={props.documentId} />
                    )
                }

            case TaskpanePages.Fields:
                if (!dgWordCanvasProxy) {
                    return <WordTaskpaneLoadingPage />
                } else {
                    return <WordFieldsCard currentField={currentField} documentId={props.documentId} />
                }

            case TaskpanePages.ClauseLibrary:
                return (
                    <ClauseLibrary documentId={props.documentId} />
                );

            default:
                console.log(`unknown page selected in taskpane: ${activePage}.`);
                return <div />
        }
    }

    return (
        <div className="wordTaskpane">
            <WordTaskPaneHeader
                menuOpen={menuOpen}
                setMenuOpen={setMenuOpen}
                selectedPage={activePage}
                onSelectPage={setActivePage} />
            <div style={{ flex: 1, overflowY: 'hidden' }}>
                {getPageContent()}
                <AaTaskpaneMenu
                    offestTop={headerHeight}
                    open={menuOpen}
                    closeCallback={() => setMenuOpen(false)}
                    documentId={props.documentId} />
            </div>
        </div>
    )
}

const TopHeader = styled.div`
    width: 100%;
    height: ${headerHeight};
    background: ${primaryThemeSettings.palette.themePrimary};
    display: flex;
    flex-direction: row;
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.15);
    justify-content: space-around;
`;

const TabContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    flex: 1;
    align-items: center;
    justify-content: center;
    gap: 2px;
`;

const PageTabContainer = styled(TabContainer)<{selected?: boolean}>`
    justify-content: initial;
    padding-top: 4px;
    box-sizing: border-box;
    border-bottom: ${props => !!props.selected && '3px solid ' + primaryTheme.palette.white};
    :hover {
        border-bottom: ${props => !props.selected && `3px solid ${primaryTheme.palette.white}66`};
    }
`;

interface WordTaskPaneHeaderProps {
    menuOpen: boolean;
    setMenuOpen: (v: boolean) => void;
    selectedPage: TaskpanePages;
    onSelectPage: (page: TaskpanePages) => void;
}

const WordTaskPaneHeader = (props: WordTaskPaneHeaderProps): JSX.Element => {
    const dgIcons = useDgIcons();
    const enabledColor = primaryTheme.palette.white;

    function taskpaneMenuIcon(): JSX.Element {
        return (
            <DgIconButton
                iconName={props.menuOpen ? dgIconNames.AssistedAuthoringMenuOpened : dgIconNames.AssistedAuthoringMenuClosed}
                height={19}
                width={28}
                color={primaryTheme.palette.white}
                shadow=''
                onClick={() => { props.setMenuOpen(!props.menuOpen) }} />
        )
    }

    const pages: ITaskpanePage[] = [
        {
            page: TaskpanePages.Clauses,
            label: "CLAUSES",
            icon: dgIconNames.AssistedAuthoringClauses,
            iconHeight: 21,
            iconWidth: 18
        },
        {
            page: TaskpanePages.Fields,
            label: "FIELDS",
            icon: dgIconNames.AssistedAuthoringFields,
            iconHeight: 21,
            iconWidth: 25
        },
        {
            page: TaskpanePages.ClauseLibrary,
            label: "CLAUSE LIBRARY",
            icon: dgIconNames.AssistedAuthoringClauseLibrary,
            iconHeight: 21,
            iconWidth: 16
        }
    ]

    return (
        <TopHeader>
            {
                pages.map(page => (
                    <PageTabContainer key={page.label} onClick={() => props.onSelectPage(page.page)} selected={props.selectedPage === page.page}>
                        {dgIcons.icon(page.icon, '', page.iconHeight, page.iconWidth, enabledColor, primaryTheme.palette.black)}
                        <AaHeaderLabelText key="hlt">{page.label}</AaHeaderLabelText>
                    </PageTabContainer>
                ))
            }
            <TabContainer key="col">
                {taskpaneMenuIcon()}
            </TabContainer>
        </TopHeader>
    );
}

const StatusBarContainer = styled.div`
    height: 10px;
    width: 100%;
`;

export const WordTaskpaneLoadingPage = (): JSX.Element => {
    return (
        <StatusBarContainer>
            <StatusBar isInfinite />
        </StatusBarContainer>
    )
}
